/* CompagniePage.jsx — Pagina pubblica /compagnie.
 *
 * Hero + griglia completa di tutte le compagnie partner Colucci con
 * informazioni complete (IVASS/RUI, paese, prodotti, link area intermediari).
 *
 * Sostituisce la sezione homepage (rimossa nella Fase 8.4).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, ExternalLink, Globe } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/compagnie-partner`;

function flagEmoji(iso2) {
  if (!iso2 || iso2.length !== 2) return "🌍";
  const A = 0x1F1E6;
  return String.fromCodePoint(...[...iso2.toUpperCase()]
    .map((c) => A + c.charCodeAt(0) - 65));
}

export default function CompagniePage() {
  const [items, setItems] = useState(null);  // null=loading, []=empty

  useEffect(() => {
    axios.get(API).then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]));
  }, []);

  return (
    <div className="col-page-bg flex flex-col" data-testid="compagnie-page">
      <Header />

      <main className="flex-1 max-w-6xl w-full mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Breadcrumb */}
        <nav className="text-xs text-slate-500 mb-4 flex items-center gap-2"
             aria-label="Breadcrumb">
          <Link to="/" className="hover:text-emerald-700 inline-flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" /> Home
          </Link>
          <span aria-hidden="true">·</span>
          <span className="font-bold text-slate-700">Compagnie Partner</span>
        </nav>

        {/* Hero */}
        <header className="text-center mb-10 sm:mb-14" data-testid="compagnie-hero">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-800 text-xs font-medium mb-3">
            <ShieldCheck className="w-3.5 h-3.5" /> Mandati attivi e collaborazioni
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-[#163321] mb-3 leading-tight">
            Le nostre Compagnie Partner
          </h1>
          <p className="text-slate-600 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
            Lavoriamo con compagnie italiane ed europee selezionate, in regola
            con <b>IVASS</b>, <b>EIOPA</b> e i registri RUI/FMA dei rispettivi
            paesi. Trasparenza totale sui mandati ai sensi dell'art. 56 Reg. IVASS 40/2018.
          </p>
        </header>

        {/* Grid */}
        {items === null ? (
          <p className="text-center text-slate-500 py-10">Caricamento…</p>
        ) : items.length === 0 ? (
          <div className="text-center py-10 col-card max-w-md mx-auto"
               data-testid="compagnie-empty">
            <Globe className="w-10 h-10 mx-auto text-slate-300 mb-3" />
            <p className="text-sm text-slate-600">
              Elenco compagnie partner in aggiornamento.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
            {items.map((c, i) => (
              <a
                key={i}
                href={c.url_riservata_intermediari || "#"}
                target={c.url_riservata_intermediari ? "_blank" : "_self"}
                rel="noopener noreferrer"
                data-testid={`compagnia-tile-${i}`}
                className="group rounded-2xl bg-white border border-slate-200 p-5 hover:border-[#163321] hover:shadow-lg transition-all flex items-start gap-4"
              >
                <div className="shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-50 to-amber-50 flex items-center justify-center text-2xl">
                  {c.logo_url ? (
                    <img src={c.logo_url} alt={c.ragione_sociale}
                         className="w-full h-full object-contain rounded-xl" />
                  ) : flagEmoji(c.paese)}
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-bold text-[#163321] text-sm md:text-base leading-tight group-hover:text-emerald-700 transition-colors">
                    {c.nome_commerciale || c.ragione_sociale}
                  </h3>
                  {c.nome_commerciale && (
                    <p className="text-xs text-slate-400 mt-0.5 truncate">
                      {c.ragione_sociale}
                    </p>
                  )}
                  <div className="flex flex-wrap items-center gap-1.5 mt-2">
                    {c.paese && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-50 text-sky-800 font-bold">
                        {flagEmoji(c.paese)} {c.paese}
                      </span>
                    )}
                    {c.numero_albo_ivass && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800">
                        IVASS {c.numero_albo_ivass}
                      </span>
                    )}
                    {c.tipo === "collaborazione_orizzontale" && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-100 text-violet-800">
                        🤝 Collaborazione orizzontale
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2.5">
                    {(c.prodotti_distribuiti || []).map((p) => (
                      <span key={p}
                            className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 uppercase tracking-wide">
                        {p.replace(/_/g, " ")}
                      </span>
                    ))}
                  </div>
                </div>
                {c.url_riservata_intermediari && (
                  <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-[#163321] shrink-0 mt-1" />
                )}
              </a>
            ))}
          </div>
        )}

        {/* Compliance note */}
        <section className="mt-10 sm:mt-14 col-card bg-slate-50"
                 data-testid="compagnie-compliance-note">
          <h2 className="text-base sm:text-lg font-bold text-[#163321] mb-2">
            Trasparenza e compliance
          </h2>
          <p className="text-sm text-slate-600 leading-relaxed">
            CHRISTIAN E CIRO COLUCCI S.A.S. (RUI A000464752) opera su mandato
            delle compagnie sopra indicate ai sensi dell'art. 119 D.Lgs. 209/2005
            (Codice delle Assicurazioni Private). I rapporti contrattuali, le
            condizioni di collaborazione e i prodotti distribuiti sono
            consultabili presso la sede agenziale. Per dettagli sull'intermediario
            consulta l'<Link to="/legal/intermediario-rui"
                            className="text-emerald-700 underline font-semibold">
            informativa precontrattuale dedicata</Link>.
          </p>
        </section>

        <div className="text-center mt-8">
          <Link to="/"
                data-testid="compagnie-back-home"
                className="inline-flex items-center gap-2 text-sm text-emerald-700 hover:underline font-semibold">
            <ArrowLeft className="w-4 h-4" /> Torna alla home
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
}
